import { colors } from '@hazadapt-git/public-core-base'
import Help from '@mui/icons-material/Help'
import MuiPopover from '@mui/material/Popover'
import { Button, PopoverOrigin, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React, { FC, useState, MouseEvent, MouseEventHandler } from 'react'

import { theme, useStyles } from '../../lib/styles/universal'
import { Markdown } from './Markdown'
import cx from 'classnames'

interface PopoverProps {
    buttonContent?: string | React.ReactNode
    popoverContent: string | React.ReactNode
    popoverPlacement?: 'left' | 'right' | 'center'
    onClick?: MouseEventHandler<HTMLDivElement>
    icon?: React.ReactNode
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    buttonClassName?: string
}

export const Popover: FC<PopoverProps> = (props: PopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }
    const handleClose = () => setAnchorEl(null)
    const open = Boolean(anchorEl)
    const { classes } = useStyles()

    const { classes: localClasses } = useLocalStyles()

    return (
        <div className={classes.popover} onClick={props.onClick}>
            <Button
                variant="text"
                onClick={handleClick}
                aria-label={
                    typeof props.buttonContent === 'string'
                        ? props.buttonContent
                        : 'More information'
                }
                className={cx(localClasses.trigger, props.buttonClassName)}
            >
                {props.icon ?? <Help htmlColor={colors.grays.NIMBUS} />}
                {props.buttonContent && (
                    <Typography component={'span'}>
                        {props.buttonContent}
                    </Typography>
                )}
            </Button>
            <MuiPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        style: {
                            backgroundColor: colors.grays.WHITESMOKE,
                            borderRadius: '1rem',
                            maxWidth: '25rem',
                        },
                    },
                }}
                anchorOrigin={
                    props.anchorOrigin ?? {
                        vertical: 'top',
                        horizontal: 'center',
                    }
                }
                transformOrigin={
                    props.transformOrigin ?? {
                        vertical: 'bottom',
                        horizontal:
                            props.popoverPlacement === 'left'
                                ? 'right'
                                : props.popoverPlacement === 'right'
                                ? 'left'
                                : 'center',
                    }
                }
            >
                {typeof props.popoverContent === 'string' ? (
                    <Markdown
                        containerClassName={localClasses.container}
                        content={props.popoverContent}
                    />
                ) : (
                    <div className={localClasses.container}>
                        {props.popoverContent}
                    </div>
                )}
            </MuiPopover>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        cursor: 'default',
        margin: '1rem',
        width: 'fit-content',
        maxWidth: '80vw',
        [theme.breakpoints.up('sm')]: {
            maxWidth: '30rem',
        },
    },
    trigger: {
        padding: '0 !important',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        minWidth: 'fit-content',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
})
